import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useEffectOnce } from 'react-use'

import agent from 'agent'
import BusyOverlay from 'components/common/BusyOverlay'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import { messageFromResponseError } from 'utils/strings'

/**
 * Parse the params from the URL callback from the SAML provider.
 * Returns them in a sane object.
 *
 * @example parseLocationSearchQuery('?SamlSessionCode=some-uuid&RelayState=%2Fservices')
 *  {
 *    SamlSessionCode: 'some-uuid', // or null
 *    RelayState: '/services' // or null
 *  }
 */
function parseLocationSearchQuery(locationSearch: string) {
  const searchParams = new URLSearchParams(locationSearch)

  return {
    RelayState: searchParams.get('RelayState'),
    SamlSessionCode: searchParams.get('SamlSessionCode')
  }
}

/**
 * the SAML provider will visit a url associated with this component.
 * It's expected to provide search query params.
 */
export const SAML: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const authState = useAuth()

  const handleError = (err) => {
    toast({
      type: 'error',
      text: messageFromResponseError(
        err,
        'Unable to authenticate using SAML. Please try again later.'
      )
    })

    navigate('/')
  }

  const handleSamlCallback = async () => {
    const { SamlSessionCode, RelayState } = parseLocationSearchQuery(
      location.search
    )

    if (!SamlSessionCode?.length || !RelayState?.length) {
      return handleError('parse-error')
    }

    try {
      const res = await agent.post('/users/auth/saml/sessions', {
        code: SamlSessionCode
      })
      await authState.startSession(res.data)
      navigate(RelayState)
    } catch (err) {
      return handleError(err)
    }
  }

  useEffectOnce(() => {
    handleSamlCallback()
  })

  return <BusyOverlay shown={true} text="Authorizing" />
}
